<script setup lang="ts">
import { THEME } from '@pkg/config/misc/theme';
import type { Image } from '@sanity/types';
import { useResolvedSectionTheme } from '@pkg/modular-blocks/frontend/useResolvedSectionTheme';
import CelebrationAnimation from './CelebrationAnimation.vue';
import Counter from './Counter.vue';
import SocialsShareGroup from '~/app/components/blocks/SocialsShareGroup.vue';

const props = defineProps<{
  theme: THEME,
  activeCampaign: boolean,
  initialPreTitle: string,
  initialTitle: string,
  initialText: string,
  confirmationPreTitle: string,
  confirmationTitle: string,
  confirmationText: string,
  ctaText: string,
  socialsTitle: string,
  statisticChoice: string,
  shareTitle: string,
  shareDescription: string,
  backgroundImage: Image,
  confirmationImage:Image,
}>();

const socialsTheme = useResolvedSectionTheme(() => THEME.LIGHT);

const { locale } = useI18n();

type CampaignCounterResponse = { _id: string; value: number };

const getCMSCounterNumber = async(): Promise<CampaignCounterResponse> =>
  await $fetch<CampaignCounterResponse>('/api/campaign-counter', {
    query: {
      statisticChoice: props.statisticChoice,
      locale: locale.value || 'en-gb',
    },
  });

const counter = await getCMSCounterNumber();

const initialNumber = ref(0);
const animationOn = ref(false);
const fireworksAnimationOn = ref(false);
const totalNumber = ref(initialNumber.value);
const numbersArray = ref([0]);
const rollNumbersAnimationOn = ref(true);
const confirmationStep = ref(!props.activeCampaign);
const title = computed(() => confirmationStep.value ? props.confirmationTitle : props.initialTitle);
const preTitle = computed(() => confirmationStep.value ? props.confirmationPreTitle : props.initialPreTitle);
const text = computed(() => confirmationStep.value ? props.confirmationText : props.initialText);
const userDataName = `userCounterNumber${locale.value.replace('-', '').toUpperCase()}`;

initialNumber.value = counter?.value || 0;

const setAnimation = () => {
  animationOn.value = true;
  setTimeout(function() {
    animationOn.value = false;
  }, 100);
};

const setNumbersArray = (currentNumber: number) => {
  numbersArray.value = currentNumber.toString().split('').map(num => Number(num));
};

const updateCount = (newCMSCounterNumber: number) => {
  fireworksAnimationOn.value = false;
  totalNumber.value = newCMSCounterNumber;
  setNumbersArray(totalNumber.value);
  setAnimation();
};

// TODO - makes fireworks togglable
const fireworksAnimation = () => {
  fireworksAnimationOn.value = true;
  setTimeout(function() {
    fireworksAnimationOn.value = false;
  }, 2500); // set the same time as the animation duration itself
};

const getUserState = () => {
  const userCounterNumber = localStorage.getItem(userDataName);
  if (userCounterNumber) {
    localStorage.setItem(userDataName, initialNumber.value.toString());
    confirmationStep.value = true;
  } else if (!props.activeCampaign) {
    confirmationStep.value = true;
  }
};

const onClick = () => {
  fireworksAnimationOn.value = false;
  confirmationStep.value = true;
  setTimeout(async() => {
    fireworksAnimation();
    const updated = await $fetch<CampaignCounterResponse>('/api/campaign-counter-increment', {
      method: 'POST',
      body: {
        statisticChoice: props.statisticChoice,
        locale: locale.value || 'en-gb',
      },
    });
    updateCount(updated.value);
    localStorage.setItem(userDataName, String(updated.value));
  }, 500);
};

const rollFromZeroToNumber: Function = () => {
  let currentNumberCipher = parseInt(numbersArray.value.join(''));
  if ((currentNumberCipher < initialNumber.value) && rollNumbersAnimationOn) {
    updateCount(currentNumberCipher);

    const returnToSlowIncrease = currentNumberCipher + 10 > initialNumber.value;

    if (initialNumber.value > 1000 && !returnToSlowIncrease) {
      currentNumberCipher = currentNumberCipher + 10;
    } else {
      currentNumberCipher++;
    }
    setNumbersArray(currentNumberCipher);
    return setInterval(() => rollFromZeroToNumber(), 100);
  } else {
    rollNumbersAnimationOn.value = false;
  }
};

const addHeroListener = () => {
  const hero = document && document.querySelector('.counter__image');
  hero?.addEventListener('click', () => !confirmationStep.value && onClick());
};

onMounted(() => {
  addHeroListener();
  rollFromZeroToNumber();
  getUserState();
});

</script>

<template>
  <div class="flex flex-col justify-center">
    <BaseImage
      v-if="confirmationStep"
      :image="confirmationImage"
      alt="counter image"
      title="counter image"
      class="counter__image"
    />
    <BaseImage
      v-if="!confirmationStep"
      :image="backgroundImage"
      alt="counter image"
      title="counter image"
      class="counter__image"
    />
    <BaseTitle
      :pre-title="preTitle"
      :title="title"
      variant="center"
      size="large"
    />
    <CelebrationAnimation v-if="fireworksAnimationOn" />
    <div class="pt-16">
      <Counter v-if="numbersArray" :numbers-array="numbersArray" :animation-on="animationOn" />
    </div>
    <div class="wrapper">
      <BaseText variant="center">
        {{ text }}
      </BaseText>
    </div>
    <div class="mt-32 flex justify-center">
      <UiButton v-if="!confirmationStep" @click="onClick">
        {{ ctaText }}
      </UiButton>
      <SocialsShareGroup
        v-else
        :title="socialsTitle"
        :theme="socialsTheme"
        :custom-title="shareTitle"
        :custom-description="shareDescription"
      />
    </div>
  </div>
</template>

<style scoped>
.wrapper {
  @screen tablet-wide {
    max-width: 600px;
  }
}
.counter__image {
  width: 180px;
  height: auto;
  align-self: center;

  @screen tablet-wide {
    width: 280px;
  }
}
</style>
